import { useAuth } from '../context/AuthContext';

/**
 * Custom hook for checking permissions in components
 */
export const usePermissions = () => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = useAuth();

  const permissions = {
    // Core auth functions
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    // Dashboard permissions
    canViewManagerDashboard: hasPermission('view_manager_dashboard'),

    // Profile permissions
    canViewOwnProfile: hasPermission('view_own_profile'),
    canEditOwnProfile: hasPermission('edit_own_profile'),

    // Leads permissions
    canViewLeads: hasPermission('view_leads'),
    canViewAssignedLeads: hasPermission('view_assigned_leads'),
    canCreateLeads: hasPermission('create_leads'),
    canEditLeads: hasPermission('edit_leads'),
    canDeleteLeads: hasPermission('delete_leads'),
    canManageLeadTeam: hasPermission('manage_lead_team'),

    // Projects permissions
    canViewProjects: hasPermission('view_projects'),
    canViewAssignedProjects: hasPermission('view_assigned_projects'),
    canCreateProjects: hasPermission('create_projects'),
    canEditProjects: hasPermission('edit_projects'),
    canDeleteProjects: hasPermission('delete_projects'),
    canManageProjectTeam: hasPermission('manage_project_team'),
    canViewProjectBudgets: hasPermission('view_project_budgets'),
    canManageProjectBudgets: hasPermission('manage_project_budgets'),

    // People permissions
    canViewPeople: hasPermission('view_people'),
    canInvitePeople: hasPermission('invite_people'),
    canEditPeople: hasPermission('edit_people'),
    canDeletePeople: hasPermission('delete_people'),
    canEditPermissions: hasPermission('edit_permissions'),

    // Directory permissions
    canViewInternalDirectory: hasPermission('view_internal_directory'),
    canViewExternalDirectory: hasPermission('view_external_directory'),
    canCreateExternalContacts: hasPermission('create_external_contacts'),
    canEditExternalContacts: hasPermission('edit_external_contacts'),

    // Schedule permissions
    canViewSchedule: hasPermission('view_schedule'),
    canViewOwnSchedule: hasPermission('view_own_schedule'),
    canCreateBookings: hasPermission('create_bookings'),
    canEditBookings: hasPermission('edit_bookings'),
    canDeleteBookings: hasPermission('delete_bookings'),

    // Timesheets permissions
    canViewTimesheets: hasPermission('view_timesheets'),
    canViewOwnTimesheets: hasPermission('view_own_timesheets'),
    canCreateTimesheets: hasPermission('create_timesheets'),
    canEditTimesheets: hasPermission('edit_timesheets'),
    canDeleteTimesheets: hasPermission('delete_timesheets'),
    canApproveTimesheets: hasPermission('approve_timesheets'),

    // Settings permissions
    canManageCompanySettings: hasPermission('manage_company_settings'),

    // Time Off permissions
    canViewOwnTimeOff: hasPermission('timeoff:view:self'),
    canViewAllTimeOff: hasPermission('timeoff:view:all'),
    canCreateOwnTimeOff: hasPermission('timeoff:create:self'),
    canEditOwnPendingTimeOff: hasPermission('timeoff:edit:self:pending'),
    canCancelOwnTimeOff: hasPermission('timeoff:cancel:self'),
    canCreateTeamTimeOff: hasPermission('timeoff:create:team'),
    canEditTeamPendingTimeOff: hasPermission('timeoff:edit:team:pending'),
    canApproveTimeOff: hasPermission('timeoff:approve'),
    canEditApprovedTimeOff: hasPermission('timeoff:edit:approved'),
    canOverrideTimeOffConflict: hasPermission('timeoff:override:conflict'),

    // Resource permissions
    canViewResources: hasPermission('view_resources'),
    canCreateResources: hasPermission('create_resources'),
    canEditResources: hasPermission('edit_resources'),
    canDeleteResources: hasPermission('delete_resources'),

    // Billing permissions
    canManageBilling: hasPermission('manage_billing'),

    // Convenience helpers - check if user has ANY of the related permissions
    canAccessLeads: hasAnyPermission('view_leads', 'view_assigned_leads'),
    canAccessProjects: hasAnyPermission('view_projects', 'view_assigned_projects'),
    canAccessSchedule: hasAnyPermission('view_schedule', 'view_own_schedule'),
    canAccessTimesheets: hasAnyPermission('view_timesheets', 'view_own_timesheets'),
    canAccessDirectory: hasAnyPermission('view_internal_directory', 'view_external_directory'),
    canAccessTimeOff: hasAnyPermission('timeoff:view:self', 'timeoff:view:all', 'timeoff:approve'),
    canAccessResources: hasAnyPermission('view_resources', 'create_resources'),
  };

  return permissions;
};

export default usePermissions;